'use client';

import { c as _c } from "react/compiler-runtime";
import { getImageProxy } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type { ChangeEvent, FormEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { ImageSearchResult } from '@/components/images/search-grid';
import { SearchGrid } from '@/components/images/search-grid';
import { validateImageUpload } from '@/components/images/validation';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Search01Icon } from '@/components/ui/icon/glyphs';
import { InputField } from '@/components/ui/input-field';
import { onEvent } from '@/events';
import type { ImageGenerationMode, ImageSearchFailureReason } from '@/events/image-generation';
import { runWithCleanup } from '@/lib/async/run-with-cleanup';
import { logger } from '@/lib/telemetry/logger';
export interface SearchTabProps {
  /** Pre-fills the search input — for avatar/hero pickers, pass the user's display name. */
  initialQuery: string;
  /** For analytics (image_search_started/completed/failed/used events). */
  mode: ImageGenerationMode;
  /**
   * Controlled current value of the search input box. Lifted to the parent
   * (IdentityImagePicker) so the user's typed query survives the picker
   * dialog being unmounted while CropModal is open.
   */
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  /**
   * Controlled most-recently-submitted query — what feeds the result grid.
   * Also lifted to the parent so the results stay rendered (and TanStack
   * Query stays cached) across crop ↔ picker round-trips.
   */
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  /**
   * Fired when the user clicks a search result. The component fetches the
   * full-size image bytes through `getImageProxy` and hands the parent a
   * ready-to-crop `File`. The parent typically routes this into
   * `<CropModal>` via its `initialFile` prop.
   */
  onPick: (file: File) => void;
}
type SearchKind = 'idle' | 'results' | 'error';
export function SearchTab(t0) {
  const $ = _c(59);
  const {
    initialQuery,
    mode,
    searchInput,
    onSearchInputChange,
    searchQuery,
    onSearchQueryChange,
    onPick
  } = t0;
  const t = useTranslations("launchpad.imageGen");
  const seededInitialInputRef = useRef(false);
  let t1;
  if ($[0] !== initialQuery || $[1] !== onSearchInputChange || $[2] !== searchInput.length) {
    t1 = () => {
      if (seededInitialInputRef.current) {
        return;
      }
      seededInitialInputRef.current = true;
      if (searchInput.length === 0 && initialQuery.length > 0) {
        onSearchInputChange(initialQuery);
      }
    };
    $[0] = initialQuery;
    $[1] = onSearchInputChange;
    $[2] = searchInput.length;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useEffect(t1, t2);
  const searchKindRef = useRef(searchQuery.length > 0 ? "results" : "idle");
  const [loadingResultUrl, setLoadingResultUrl] = useState(null);
  const hasAutoFiredRef = useRef(false);
  const searchStartTimeRef = useRef(0);
  const lastSeenPageCountRef = useRef(0);
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Map();
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const fullSizeFetchCacheRef = useRef(t3);
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = function fetchImageBlob(imageUrl) {
      const proxiedUrl = getImageProxy({
        url: imageUrl
      });
      const existing = fullSizeFetchCacheRef.current.get(proxiedUrl);
      if (existing !== undefined) {
        return existing;
      }
      const promise = fetch(proxiedUrl).then(_temp);
      fullSizeFetchCacheRef.current.set(proxiedUrl, promise);
      promise.then(undefined, () => {
        fullSizeFetchCacheRef.current.delete(proxiedUrl);
      });
      return promise;
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const fetchImageBlob = t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = async function fetchSelectableFile(result) {
      const candidateUrls = result.fullUrl === result.thumbnailUrl ? [result.fullUrl] : [result.fullUrl, result.thumbnailUrl];
      let selectionError = new Error("Image search result has no selectable source");
      for (const imageUrl_0 of candidateUrls) {
        const blob = await fetchImageBlob(imageUrl_0).then(_temp2, err => {
          selectionError = err;
          return null;
        });
        if (blob === null) {
          continue;
        }
        const ext = blob.type.split("/")[1] ?? "png";
        const file = new File([blob], `search-result.${ext}`, {
          type: blob.type.length > 0 ? blob.type : "image/png"
        });
        const validation = validateImageUpload(file);
        if (validation === null) {
          return file;
        }
        selectionError = new Error(`Image source validation failed: ${validation}`);
      }
      throw selectionError;
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const fetchSelectableFile = t5;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = function handleHoverPrefetch(result_0) {
      fetchImageBlob(result_0.fullUrl).catch(() => {
        const proxiedUrl_0 = getImageProxy({
          url: result_0.fullUrl
        });
        fullSizeFetchCacheRef.current.delete(proxiedUrl_0);
      });
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const handleHoverPrefetch = t6;
  let t7;
  if ($[9] !== initialQuery || $[10] !== mode || $[11] !== onSearchQueryChange || $[12] !== searchQuery.length) {
    t7 = () => {
      if (hasAutoFiredRef.current) {
        return;
      }
      if (searchQuery.length > 0) {
        hasAutoFiredRef.current = true;
        return;
      }
      const trimmed = initialQuery.trim();
      if (trimmed.length === 0) {
        return;
      }
      hasAutoFiredRef.current = true;
      searchStartTimeRef.current = Date.now();
      lastSeenPageCountRef.current = 0;
      onSearchQueryChange(trimmed);
      searchKindRef.current = "results";
      onEvent("image_search_started", {
        mode,
        queryLength: trimmed.length,
        autoTriggered: true
      });
    };
    $[9] = initialQuery;
    $[10] = mode;
    $[11] = onSearchQueryChange;
    $[12] = searchQuery.length;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = [];
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  useEffect(t7, t8);
  let t9;
  if ($[15] !== mode || $[16] !== onSearchQueryChange || $[17] !== searchInput || $[18] !== searchQuery) {
    t9 = function handleSearchSubmit(e) {
      e.preventDefault();
      const trimmed_0 = searchInput.trim();
      if (trimmed_0.length === 0 || trimmed_0.length > 200) {
        return;
      }
      searchKindRef.current = "results";
      searchStartTimeRef.current = Date.now();
      lastSeenPageCountRef.current = 0;
      if (searchQuery !== trimmed_0) {
        onSearchQueryChange(trimmed_0);
      }
      onEvent("image_search_started", {
        mode,
        queryLength: trimmed_0.length,
        autoTriggered: false
      });
    };
    $[15] = mode;
    $[16] = onSearchQueryChange;
    $[17] = searchInput;
    $[18] = searchQuery;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const handleSearchSubmit = t9;
  let t10;
  if ($[20] !== loadingResultUrl || $[21] !== mode || $[22] !== onPick) {
    t10 = async function handleSelect(result_1) {
      if (loadingResultUrl !== null) {
        return;
      }
      setLoadingResultUrl(result_1.fullUrl);
      await runWithCleanup(async () => {
        ;
        try {
          const file_0 = await fetchSelectableFile(result_1);
          onEvent("image_search_used", {
            mode,
            sourceDomain: result_1.sourceDomain
          });
          onPick(file_0);
        } catch (t11) {
          const err_0 = t11;
          const message = err_0 instanceof Error ? err_0.message : String(err_0);
          logger.error("image-search fetch failed", {
            error: message.slice(0, 200)
          });
        }
      }, () => setLoadingResultUrl(null));
    };
    $[20] = loadingResultUrl;
    $[21] = mode;
    $[22] = onPick;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const handleSelect = t10;
  let t11;
  if ($[24] !== t) {
    t11 = t("search.placeholder");
    $[24] = t;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== onSearchInputChange) {
    t12 = e_0 => {
      onSearchInputChange(e_0.target.value);
    };
    $[26] = onSearchInputChange;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t) {
    t13 = t("search.placeholder");
    $[28] = t;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== searchInput || $[31] !== t11 || $[32] !== t12 || $[33] !== t13) {
    t14 = <InputField fullWidth={true} placeholder={t11} value={searchInput} onChange={t12} maxLength={200} aria-label={t13} data-testid="image-search-input" />;
    $[30] = searchInput;
    $[31] = t11;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== searchInput) {
    t15 = searchInput.trim();
    $[35] = searchInput;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  const t16 = t15.length === 0;
  let t17;
  if ($[37] !== t) {
    t17 = t("search.cta.search");
    $[37] = t;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t16 || $[40] !== t17) {
    t18 = <AnimatedButton type="submit" prefix={Search01Icon} data-testid="image-search-submit" disabled={t16}>{t17}</AnimatedButton>;
    $[39] = t16;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== handleSearchSubmit || $[43] !== t14 || $[44] !== t18) {
    t19 = <form onSubmit={handleSearchSubmit} className="flex gap-2 p-1">{t14}{t18}</form>;
    $[42] = handleSearchSubmit;
    $[43] = t14;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== handleSelect) {
    t20 = r => {
      handleSelect(r);
    };
    $[46] = handleSelect;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  let t22;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = _reason => {
      searchKindRef.current = "error";
    };
    t22 = () => {
      if (searchKindRef.current !== "results") {
        searchKindRef.current = "results";
      }
    };
    $[48] = t21;
    $[49] = t22;
  } else {
    t21 = $[48];
    t22 = $[49];
  }
  let t23;
  if ($[50] !== loadingResultUrl || $[51] !== mode || $[52] !== searchQuery || $[53] !== t || $[54] !== t20) {
    t23 = <SearchGrid query={searchQuery} mode={mode} startTimeRef={searchStartTimeRef} lastSeenPageCountRef={lastSeenPageCountRef} loadingResultUrl={loadingResultUrl} onPrefetch={handleHoverPrefetch} onSelect={t20} onError={t21} onSuccess={t22} t={t} />;
    $[50] = loadingResultUrl;
    $[51] = mode;
    $[52] = searchQuery;
    $[53] = t;
    $[54] = t20;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== t19 || $[57] !== t23) {
    t24 = <div className="flex flex-col gap-4">{t19}{t23}</div>;
    $[56] = t19;
    $[57] = t23;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  return t24;
}
function _temp2(value) {
  return value;
}
async function _temp(response) {
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return response.blob();
}